import React, { useMemo, useRef } from 'react';
import { useEffect } from 'react';
import { PDPLayout } from '~/features/product-details-page/PDPLayout';
import { ProductSchema } from '~/features/product-details-page/components/ProductSchema';
import { ProductsCarousels } from '~/features/product-details-page/components/ProductsCarousels';
import { useProduct } from '~/features/product-details-page/hooks/usePDPStore';
import { getVariantSummary } from '~/features/product-details-page/hooks/useProductDetailsStore';
import { P50ProductDetailsPage as Props } from '~/features/product-details-page/pdp.definitions';
import { Modules } from '~/lib/data-contract/auto-generated/api-model';
import { _transformProductToDataLayer } from '~/shared/hooks/useEvents/helpers';
import { useEvents } from '~/shared/hooks/useEvents/useEvents';
import { useFrame } from '~/shared/utils';
import { DynamicBlockList } from '~/templates/blocks/components/DynamicBlockList/DynamicBlockList';
import { PDPMetaFields } from '~/templates/pages/components/P50ProductDetailsPage';
import { Product } from '~/lib/data-contract/salesforce';
import { YotpoReview } from '~/shared/components/YotpoReview';
import { ViewedProducts } from '~/features/product-details-page/components/ViewedProducts';

export const P50ProductDetailsPage = (props: Props) => {
    const {
        pageElements,
        bottomPageElements,
        data: { product: productData },
        breadcrumb,
    } = props;
    const { product } = useProduct();
    const { data: frame } = useFrame();
    const { productDetailviewEvent } = useEvents(frame);
    const yotpoReviewRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (product) {
            const [, ...categories] = breadcrumb?.items || [];
            const gaBreadcrumbString = categories
                .slice(0, -1)
                .map((category) => `${category.title}#${category.title}`)
                .join(';');

            productDetailviewEvent(_transformProductToDataLayer(product, 1), gaBreadcrumbString);
        }
    }, [product?.id]);

    const variantSummary = useMemo(() => getVariantSummary(productData as Product), [productData]);

    return (
        <>
            {product && <PDPMetaFields product={product} />}

            <ProductSchema breadcrumb={breadcrumb} />

            <DynamicBlockList
                productId={productData?.id}
                productBrand={productData?.brand}
                productCategory={productData?.c_ProductMainCategory}
                elements={pageElements as Modules[]}
            />

            <PDPLayout variantSummary={variantSummary} yotpoReviewRef={yotpoReviewRef} />

            <DynamicBlockList
                productId={productData?.id}
                productBrand={productData?.brand}
                productCategory={productData?.c_ProductMainCategory}
                elements={bottomPageElements as Modules[]}
            />

            <ProductsCarousels />

            <ViewedProducts />

            {product?.id && product?.name && product?.c_yotpoReviewCount && (
                <>
                    <div ref={yotpoReviewRef}></div>
                    <YotpoReview productId={product.id} productName={product.name} />
                </>
            )}
        </>
    );
};
